import "./LabelingRules.scss";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { RouteComponentProps, navigate } from "@reach/router";
import { useSelector } from "react-redux";
import { MdAdd, MdDelete, MdSave, MdErrorOutline, MdSmsFailed, MdCheck } from "react-icons/md";
import { Nav } from "react-bootstrap";
import * as R from "ramda";
import { SegmentationMappingLabelRule } from "@blisspointmedia/bpm-types/dist/SegmentationMapping";
import useLocation from "../utils/hooks/useLocation";
import * as UserRedux from "../redux/user";
import {
  Button,
  ButtonType,
  ButtonFrameworkVariant,
  Dropdown,
  FilterBar,
  Spinner,
  FullPageSpinner,
  DropdownToggleType,
} from "../Components";
import AlertDialog, { AlertDialogVariants } from "../Components/AlertDialog/AlertDialog";
import { SegmentationMappingLambdaFetch, awaitJSON } from "../utils/fetch-utils";
import { useStateFunction } from "../utils/hooks/useData";
import { Brand60, SubBrand1, Warning500, Success700 } from "../utils/colors";
import LabelingRulesTable from "./LabelingRulesTable";
import LabelingRulesModal from "./LabelingRulesModal";
import { SystemGeneratedSegmentNames } from "./CustomSegments";
import PendingLabelingRuleChanges from "./PendingLabelingRuleChanges";
import { CustomSegmentsData } from "./SegmentationMapping";

export const omitMetaFields = (rule: SegmentationMappingLabelRule): any =>
  R.omit(["updatedAt", "lastUpdatedBy", "segmentName", "segmentValueName", "ifStatement"], rule);

export const getSegmentName = (segmentId: number, customSegments: CustomSegmentsData[]): string => {
  return (
    customSegments?.find(segment => Number(segment.segmentId) === segmentId)?.segmentName || ""
  );
};

export const getSegmentValue = (valueId: number, customSegments: CustomSegmentsData[]): string => {
  return (
    customSegments
      ?.flatMap(segment => segment.values)
      .find(value => Number(value.valueId) === valueId)?.valueName || ""
  );
};

const ALERT_TYPES = {
  NAV_AWAY: "nav",
  DISCARD: "discard",
  SUCCESS: "success",
  ERROR: "error",
};

interface LabelingRulesProps {
  data: any;
  rules: any[];
  customSegments: any;
  dataGranularity: "ad" | "ad_group" | "campaign";
  onSave: any;
  setHasUnsavedChanges?: (hasChanges: boolean) => void;
}

const LabelingRules = ({
  data,
  rules,
  customSegments,
  dataGranularity = "campaign",
  onSave = () => {},
  setHasUnsavedChanges = () => {},
}: LabelingRulesProps & RouteComponentProps): JSX.Element => {
  const user = useSelector(UserRedux.fullNameSelector);
  const { company } = useLocation();
  const [error, setError] = useState<Error>();

  const [initializing, setInitializing] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>("all");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const [originalRules, setOriginalRules] = useState<SegmentationMappingLabelRule[]>(rules);
  const [rulesList, setRulesList] = useState<SegmentationMappingLabelRule[]>(rules);

  const [showPendingChanges, setShowPendingChanges] = useState<boolean>(false);

  const newRules = useMemo(() => {
    return rulesList?.filter(r => !r.id)?.sort((a, b) => a.setSegmentId - b.setSegmentId) || [];
  }, [rulesList]);

  const deletedRules = useMemo(() => {
    return (
      originalRules
        .filter(orig => !rulesList?.find(r => r.id === orig.id))
        .sort((a, b) => a.setSegmentId - b.setSegmentId) || []
    );
  }, [originalRules, rulesList]);

  const editedRules = useMemo(() => {
    return (
      rulesList
        ?.filter(rule => {
          if (!rule.id) {
            return false;
          }
          const original = originalRules.find(o => o.id === rule.id);
          if (!original) {
            return false;
          }
          return !R.equals(omitMetaFields(rule), omitMetaFields(original));
        })
        .sort((a, b) => a.setSegmentId - b.setSegmentId) || []
    );
  }, [rulesList, originalRules]);

  const getFreshRuleData = useCallback(async () => {
    try {
      const res = await SegmentationMappingLambdaFetch("/getSegmentLabelingRules", {
        params: {
          company,
        },
      });
      const ruleDataLambdaResult = await awaitJSON<SegmentationMappingLabelRule[]>(res);
      const ruleData: SegmentationMappingLabelRule[] = ruleDataLambdaResult;
      setOriginalRules(ruleData);
      setRulesList(ruleData);
      onSave(ruleData || []);
    } catch (e) {
      const error: Error = e as Error;
      setError(error);
    }
  }, [company, onSave]);

  useEffect(() => {
    if (rules && rules.length > 0 && originalRules.length === 0) {
      setInitializing(true);
      setOriginalRules([...rules]);
      setRulesList([...rules]);
      setInitializing(false);
    }
  }, [originalRules.length, rules]);

  const simpleCustomSegments = useMemo(() => {
    return customSegments?.filter(segment => {
      const systemNames = Object.values(SystemGeneratedSegmentNames).map(name => String(name));
      return !systemNames.includes(segment.segmentName);
    });
  }, [customSegments]);

  const pageNavs = useMemo(() => {
    const tabs = [{ label: "All Segments", key: "all" }];
    const customTabs = simpleCustomSegments?.map(segment => ({
      label: segment.segmentName,
      key: segment.segmentName,
    }));
    return customTabs ? tabs.concat(customTabs) : tabs;
  }, [simpleCustomSegments]);

  const [screenWidth, setScreenWidth] = useState<number>(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [screenWidth]);

  const visibleTabs = useMemo(() => {
    const avgCharLength = 13;
    const padding = 24;

    let tabWidthSum = 0;
    let maxVisibleTabs = 0;
    let maxOverflowLabelLength =
      avgCharLength * Math.max(...pageNavs.map(item => item.label.length));

    while (maxVisibleTabs < pageNavs.length && tabWidthSum < screenWidth - maxOverflowLabelLength) {
      // Padding between each tab is 24px, estimating that each character takes up 12px
      tabWidthSum += padding + avgCharLength * pageNavs[maxVisibleTabs].label.length;
      maxVisibleTabs++;
    }
    if (maxVisibleTabs === pageNavs.length) {
      // if there's no overflow, we don't need room for the "More..." button, so we can display one more tab
      maxVisibleTabs += 1;
    }
    return pageNavs.slice(0, maxVisibleTabs);
  }, [pageNavs, screenWidth]);

  const overflowTabs = useMemo(() => {
    return pageNavs.slice(visibleTabs.length);
  }, [pageNavs, visibleTabs]);

  const findSegmentName = useCallback(
    (segmentId: number): string => {
      return getSegmentName(segmentId, customSegments);
    },
    [customSegments]
  );

  const findSegmentValue = useCallback(
    (valueId: number): string => {
      return getSegmentValue(valueId, simpleCustomSegments);
    },
    [simpleCustomSegments]
  );

  const dataToUse = useMemo(() => {
    const modifiedData = rulesList?.map(rule => {
      const segmentName = findSegmentName(rule.setSegmentId);
      const segmentValueName = findSegmentValue(rule.setSegmentValueId);
      const { filterTokens } = rule;
      const ifStatement =
        rule.isAdvanced && Array.isArray(filterTokens)
          ? filterTokens.map(token => token.label).join(" ")
          : `* is like ${
              Array.isArray(filterTokens)
                ? filterTokens.map(filter => filter.label).join(" or ")
                : filterTokens
            }`;
      return { ...rule, segmentName, segmentValueName, ifStatement };
    });
    modifiedData?.sort((a, b) => {
      if (a.setSegmentId !== b.setSegmentId) {
        return a.setSegmentId - b.setSegmentId;
      }
      return a.rulePriority - b.rulePriority;
    });
    return activeTab === "all"
      ? {
          data: modifiedData,
        }
      : { data: modifiedData?.filter(rule => rule.segmentName === activeTab) };
  }, [activeTab, findSegmentName, findSegmentValue, rulesList]);

  const [filter, setFilter] = useStateFunction<(line) => boolean>(() => true);

  const filteredData = useMemo(() => {
    return dataToUse.data?.filter(filter);
  }, [dataToUse.data, filter]);

  const filterBarOptions = useMemo(() => {
    return [
      { name: "name", label: "Rule ID" },
      { name: "segmentName", label: "Segment" },
      { name: "segmentValueName", label: "Segment Value" },
      { name: "ifStatement", label: "If statement" },
      { name: "createdBy", label: "Created By" },
      { name: "lastUpdatedBy", label: "Last Updated By" },
    ];
  }, []);

  const newPriority = useCallback(
    (newRule: any) => {
      if (!dataToUse.data?.length) {
        return 1;
      }

      const relevantRules = rulesList.filter(r => r?.setSegmentId === newRule?.setSegmentId);

      return relevantRules.length ? Math.max(...relevantRules.map(r => r.rulePriority)) + 1 : 1;
    },
    [dataToUse.data?.length, rulesList]
  );

  const [ruleToEdit, setRuleToEdit] = useState<SegmentationMappingLabelRule | null>(null);
  const [ruleToCopy, setRuleToCopy] = useState<SegmentationMappingLabelRule | null>(null);

  const resetModalState = useCallback(() => {
    setRuleToEdit(null);
    setRuleToCopy(null);
  }, []);

  useEffect(() => {
    if (ruleToCopy) {
      setShowModal(true);
    }
  }, [ruleToCopy]);

  const handleRuleAdd = useCallback(
    (rule: SegmentationMappingLabelRule) => {
      const date = new Date().toISOString();
      const newRule = {
        ...rule,
        isActive: true,
        createdBy: user,
        lastUpdatedBy: user,
        createdAt: date,
        updatedAt: date,
        rulePriority: newPriority(rule),
        company,
        segmentName: getSegmentName(rule.setSegmentId, simpleCustomSegments),
        segmentValueName: getSegmentValue(rule.setSegmentValueId, simpleCustomSegments),
      };
      setRulesList([...(rulesList || []), newRule]);
      setRuleToCopy(null);
      setShowModal(false);
    },
    [company, newPriority, rulesList, simpleCustomSegments, user]
  );

  const handleRuleEdit = useCallback(
    (editedRule: SegmentationMappingLabelRule) => {
      const newlyEditedRule = {
        ...editedRule,
      };

      let updatedRules;
      let originalRule;

      if (editedRule.id) {
        originalRule = originalRules?.find(rule => rule.id === editedRule.id);
        if (!originalRule) {
          return;
        }
        // Remove metadata fields from both rules before comparing
        const cleanedNew = omitMetaFields(newlyEditedRule);
        const cleanedOriginal = omitMetaFields(originalRule);

        const isSubstantivelyDifferent = !R.equals(cleanedNew, cleanedOriginal);

        if (isSubstantivelyDifferent) {
          updatedRules = rulesList?.map(rule =>
            rule.id === newlyEditedRule.id ? { ...rule, ...newlyEditedRule } : rule
          );
        } else {
          updatedRules = rulesList?.map(rule =>
            rule.id === newlyEditedRule.id ? originalRule : rule
          );
        }
      } else {
        updatedRules = rulesList?.map(rule =>
          rule.createdAt === editedRule.createdAt ? { ...rule, ...newlyEditedRule } : rule
        );
      }

      const newlyUpdatedRules = updatedRules?.map(rule => {
        const segmentName = getSegmentName(rule.setSegmentId, simpleCustomSegments);
        const segmentValueName = getSegmentValue(rule.setSegmentValueId, simpleCustomSegments);
        return { ...rule, segmentName, segmentValueName };
      });

      setRulesList(newlyUpdatedRules);
      setRuleToEdit(null);
      setShowModal(false);
    },
    [originalRules, rulesList, simpleCustomSegments]
  );

  const handleRuleReorder = useCallback(
    (reorderedRules: SegmentationMappingLabelRule[]) => {
      const reorderedWithUpdatedPriority = reorderedRules.map((rule, index) => {
        const newPriority = index + 1;
        return rule.rulePriority !== newPriority
          ? {
              ...rule,
              rulePriority: newPriority,
              updatedAt: new Date().toISOString(),
              lastUpdatedBy: user,
            }
          : rule;
      });

      setRulesList(prevRules =>
        prevRules.map(rule => {
          const updated = reorderedWithUpdatedPriority.find(
            r => (r.id && r.id === rule.id) || (!r.id && r.createdAt === rule.createdAt)
          );
          return updated ? { ...rule, rulePriority: updated.rulePriority } : rule;
        })
      );
    },
    [user]
  );

  const handleRuleDelete = useCallback(
    (rule: SegmentationMappingLabelRule) => {
      const newRuleList = rule.id
        ? rulesList?.filter(r => r.id !== rule.id)
        : rulesList?.filter(r => r.createdAt !== rule.createdAt);
      setRulesList(newRuleList || []);
    },
    [rulesList, setRulesList]
  );

  const hasPendingChanges = useMemo(() => {
    return newRules.length > 0 || editedRules.length > 0 || deletedRules.length > 0;
  }, [newRules.length, editedRules.length, deletedRules.length]);

  useEffect(() => {
    setHasUnsavedChanges(hasPendingChanges);
  }, [hasPendingChanges, setHasUnsavedChanges]);

  const pendingChangesCount = useMemo(() => {
    return newRules.length + editedRules.length + deletedRules.length;
  }, [newRules.length, editedRules.length, deletedRules.length]);

  const handleClearPendingChanges = useCallback(() => {
    setRulesList(originalRules ? [...originalRules] : []);
  }, [setRulesList, originalRules]);

  const save = useCallback(async () => {
    try {
      setSaving(true);
      const body = {
        company,
        newRules,
        updatedRules: editedRules,
        deletedRules: deletedRules.map(rule => rule.id),
      };
      await SegmentationMappingLambdaFetch("/bulkUpdateSegmentMappingLabelRules", {
        method: "POST",
        body,
      });
      await getFreshRuleData();
      setSaving(false);
      setShowPendingChanges(false);
      setAlertType(ALERT_TYPES.SUCCESS);
      setShowAlertModal(true);
    } catch (e) {
      const error: Error = e as Error;
      setSaving(false);
      let ruleMessage = `Failed to update: ${error.message}`;
      if (
        error.message ===
        'duplicate key value violates unique constraint "segment_mapping_label_rules_company_name_idx".'
      ) {
        ruleMessage = `${ruleMessage} At least some of the rules you entered are already in the database`;
      }
      setError(new Error(ruleMessage));
      setAlertType(ALERT_TYPES.ERROR);
      setShowAlertModal(true);
    }
  }, [company, newRules, editedRules, deletedRules, getFreshRuleData, setError]);

  const [showAlertModal, setShowAlertModal] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<string>(ALERT_TYPES.NAV_AWAY);
  const [navToTab, setNavToTab] = useState<string>("");

  const alertModalParams = useMemo(() => {
    return {
      nav: {
        title: "Are you sure you want to leave?",
        body: "If you leave now, your changes will not be saved.",
        icon: <MdSmsFailed color={SubBrand1} size={75} />,
        variant: AlertDialogVariants.ERROR,
        primaryButtonLabel: "Stay",
        secondaryButtonLabel: "Leave",
        primaryAction: () => setShowAlertModal(false),
        secondaryAction: () => {
          setActiveTab(navToTab);
          setRulesList(originalRules);
          setShowAlertModal(false);
        },
      },
      discard: {
        title: "Discard changes?",
        body: "Any edits you've made won't be saved and you will have to start over.",
        icon: <MdDelete color={Warning500} size={75} />,
        variant: AlertDialogVariants.WARNING,
        primaryButtonLabel: "Discard",
        secondaryButtonLabel: "Cancel",
        primaryAction: () => {
          handleClearPendingChanges();
          setShowAlertModal(false);
        },
        secondaryAction: () => setShowAlertModal(false),
      },
      success: {
        title: "Segment Labeling Rules Saved!",
        body: `Go to the labeling page to label ${dataGranularity.replace(
          "_",
          " "
        )}s with these rules.`,
        icon: <MdCheck color={Success700} size={75} />,
        variant: AlertDialogVariants.SUCCESS,
        primaryButtonLabel: "Go to labeling page",
        secondaryButtonLabel: "Stay",
        primaryAction: () => {
          handleClearPendingChanges();
          setShowAlertModal(false);
          navigate(`${window.location.origin}/${company}/segmentation-mapping/labeling`);
        },
        secondaryAction: () => {
          setShowAlertModal(false);
          handleClearPendingChanges();
        },
      },
      error: {
        title: "Error",
        body: (
          <div>
            <div>There was an error saving your changes:</div>
            <div className="segmentLabelingRuleErrorMessage">{error?.message}</div>
          </div>
        ),
        icon: <MdSmsFailed color={SubBrand1} size={75} />,
        variant: AlertDialogVariants.ERROR,
        primaryButtonLabel: "Okay",
        secondaryButtonLabel: undefined,
        primaryAction: () => setShowAlertModal(false),
        secondaryAction: undefined,
      },
    };
  }, [
    company,
    dataGranularity,
    error?.message,
    handleClearPendingChanges,
    navToTab,
    originalRules,
  ]);

  return data ? (
    <div className="labelingRulesPage">
      <LabelingRulesModal
        rule={ruleToEdit ? ruleToEdit : ruleToCopy ? ruleToCopy : undefined}
        show={showModal}
        setShow={show => {
          if (!show) {
            resetModalState();
            setShowModal(false);
          } else {
            setShowModal(true);
          }
        }}
        onRuleSave={ruleToEdit ? handleRuleEdit : handleRuleAdd}
        customSegments={customSegments}
        dataGranularity={dataGranularity}
        isNew={!ruleToEdit || !R.isNil(ruleToCopy)}
        data={data}
        rules={rulesList || []}
        activeTab={activeTab}
      />
      <AlertDialog
        title={alertModalParams[alertType].title}
        body={alertModalParams[alertType].body}
        icon={alertModalParams[alertType].icon}
        variant={alertModalParams[alertType].variant}
        show={showAlertModal}
        primaryButtonLabel={alertModalParams[alertType].primaryButtonLabel}
        secondaryButtonLabel={alertModalParams[alertType].secondaryButtonLabel}
        onHide={alertModalParams[alertType].primaryAction}
        secondaryAction={alertModalParams[alertType].secondaryAction}
      />
      <Nav
        activeKey={activeTab}
        onSelect={value => {
          const valueTab = value as keyof typeof value;
          setNavToTab(valueTab);
          if (!valueTab || valueTab === activeTab) {
            return;
          }
          if (hasPendingChanges) {
            setShowAlertModal(true);
            setAlertType(ALERT_TYPES.NAV_AWAY);
          } else {
            setActiveTab(valueTab);
            setNavToTab("");
          }
        }}
      >
        <div className="navTabs">
          {visibleTabs.map(nav => (
            <Nav.Item key={nav.key}>
              <Nav.Link eventKey={nav.key}>{nav.label}</Nav.Link>
            </Nav.Item>
          ))}
        </div>
        {overflowTabs.length > 0 && (
          <div className="navOverflow">
            <Nav.Item>
              <Dropdown
                className="navDropdown"
                align="right"
                options={overflowTabs.map(nav => ({
                  label: nav.label,
                  value: nav.key,
                }))}
                onChange={value => {
                  setActiveTab(value);
                  setNavToTab("");
                }}
                placeholder="More..."
                value={
                  R.includes(
                    activeTab,
                    overflowTabs.map(nav => nav.key)
                  )
                    ? activeTab
                    : ""
                }
                type={DropdownToggleType.EMPTY}
              />
            </Nav.Item>
          </div>
        )}
      </Nav>
      <div className="labelingRulesBody">
        {initializing && <FullPageSpinner />}
        <div className="filterBarContainer">
          <FilterBar
            options={filterBarOptions}
            lines={dataToUse.data || []}
            onFilter={setFilter}
            hasAdvanced
          />
          <div className="ruleControls">
            <Button
              type={ButtonType.FILLED}
              variant={ButtonFrameworkVariant.LEADING_ICON}
              icon={<MdAdd />}
              onClick={() => {
                resetModalState();
                setShowModal(true);
              }}
            >
              {activeTab === "all" ? "New Rule" : `New ${activeTab} Rule`}
            </Button>
          </div>
        </div>
        <LabelingRulesTable
          rules={filteredData || []}
          activeTab={activeTab}
          customSegments={customSegments}
          dataGranularity={dataGranularity}
          onRuleDelete={handleRuleDelete}
          onLiveToggle={rule =>
            handleRuleEdit({
              ...rule,
              isActive: !rule.isActive,
            })
          }
          setModalShow={show => {
            if (show) {
              resetModalState();
              setShowModal(true);
            }
          }}
          setRuleToEdit={rule => {
            resetModalState();
            setRuleToEdit(rule);
          }}
          setRuleToCopy={rule => {
            resetModalState();
            setRuleToCopy(rule);
          }}
          onRuleReorder={handleRuleReorder}
        />
        <div className="labelingRuleChangesControls">
          <div className="pendingChangesControls">
            <div className="unsavedChanges">
              {hasPendingChanges && (
                <Button
                  type={ButtonType.OUTLINED}
                  variant={ButtonFrameworkVariant.LEADING_ICON}
                  design="secondary"
                  className="danger"
                  icon={<MdErrorOutline />}
                  onClick={() => {
                    setShowPendingChanges(true);
                  }}
                >
                  See {pendingChangesCount} Unsaved Change{pendingChangesCount > 1 ? "s" : ""}
                </Button>
              )}
            </div>
            <>
              <Button
                type={ButtonType.OUTLINED}
                variant={ButtonFrameworkVariant.LEADING_ICON}
                onClick={() => {
                  setAlertType(ALERT_TYPES.DISCARD);
                  setShowAlertModal(true);
                }}
                icon={<MdDelete color={Brand60} />}
                disabled={!hasPendingChanges || saving}
              >
                Discard Changes
              </Button>
              <Button
                type={ButtonType.FILLED}
                variant={ButtonFrameworkVariant.LEADING_ICON}
                onClick={save}
                disabled={saving || !hasPendingChanges}
                icon={<MdSave />}
              >
                {saving ? <Spinner /> : "Save"}
              </Button>
            </>
          </div>
        </div>
        {showPendingChanges && hasPendingChanges && (
          <PendingLabelingRuleChanges
            originalRules={originalRules || []}
            rulesList={rulesList || []}
            setRulesList={setRulesList}
            editedRules={editedRules}
            newRules={newRules}
            deletedRules={deletedRules}
            setShowPendingChanges={setShowPendingChanges}
            customSegments={customSegments}
          />
        )}
      </div>
    </div>
  ) : (
    <FullPageSpinner />
  );
};

export default LabelingRules;
